import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLines, FadeUp } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { Footer } from "../components/Footer";
import { useLang } from "../lib/i18n";
import { fetchProducts, worldFromSlug } from "../lib/api";

export const THEMES = {
  street: {
    page: "bg-[var(--ink)] text-white",
    title: "f-street uppercase text-[22vw] md:text-[15vw] leading-[0.8]",
    kicker: "text-[var(--acid)]",
    banner: "/images/hero_street.jpg",
    other: { to: "/collections/old-money", img: "/images/hero_oldmoney.jpg", key: "oldmoney" },
  },
  oldmoney: {
    page: "bg-[var(--ivory)] text-[var(--navy)]",
    title: "f-serif italic text-[18vw] md:text-[12vw] leading-[0.9]",
    kicker: "text-[var(--gold)]",
    banner: "/images/hero_oldmoney.jpg",
    other: { to: "/collections/streetwear", img: "/images/hero_street.jpg", key: "street" },
  },
};

const LAYOUT = ["md:col-span-5", "md:col-span-4 md:col-start-8 md:mt-40", "md:col-span-4 md:col-start-2", "md:col-span-5 md:col-start-7 md:mt-24"];

const Banner = ({ th, world }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const street = world === "street";
  return (
    <section ref={ref} className="relative h-[92svh] overflow-hidden">
      <motion.img style={{ y, scale: 1.1 }} src={th.banner} alt={t(world)} className="absolute inset-0 w-full h-full object-cover" />
      <div className={`absolute inset-0 ${street ? "bg-gradient-to-b from-black/50 via-black/20 to-[var(--ink)]" : "bg-gradient-to-b from-black/20 via-transparent to-[var(--ivory)]"}`} />
      <div className={`absolute bottom-0 inset-x-0 px-5 md:px-10 pb-6 ${street ? "text-white" : "text-[var(--navy)]"}`}>
        <p className={`f-mono text-[11px] uppercase tracking-[0.3em] mb-4 ${th.kicker} ${street ? "flicker" : ""}`}>{street ? "● 01 / SS26" : "◦ 02 / SS26"}</p>
        <RevealLines testid="collection-title" inView={false} delay={0.1} lines={[t(world)]} className={th.title} />
      </div>
    </section>
  );
};

const CrossOver = ({ th }) => {
  const { t } = useLang();
  return (
    <Link to={th.other.to} data-testid="collection-crossover-link" className="group relative block h-[60vh] overflow-hidden">
      <img src={th.other.img} alt={t(th.other.key)} className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform [transition-duration:1600ms] group-hover:scale-110" />
      <div className="absolute inset-0 bg-black/45 transition-colors duration-700 group-hover:bg-black/25" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-5">
        <p className="f-mono text-[11px] uppercase tracking-[0.3em] mb-5">{t("crossTo")}</p>
        <p className={th.other.key === "street" ? "f-street uppercase text-7xl md:text-[10rem] leading-none" : "f-serif italic text-7xl md:text-[9rem] leading-none"}>{t(th.other.key)}</p>
      </div>
    </Link>
  );
};

export default function Collection() {
  const { slug } = useParams();
  const world = worldFromSlug(slug);
  const th = THEMES[world];
  const { t } = useLang();
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    fetchProducts(world).then(setItems);
  }, [world]);
  return (
    <main data-testid={`collection-page-${world}`} className={th.page}>
      <Banner key={world} th={th} world={world} />
      <section className="px-5 md:px-10 pt-16 md:pt-24 pb-28 md:pb-40">
        <div className="grid grid-cols-12 gap-6 mb-20 md:mb-32">
          <p className={`col-span-12 md:col-span-3 f-mono text-[11px] uppercase tracking-[0.3em] ${th.kicker}`}>
            {items ? `${items.length} ${t("pieces")}` : t("loading")}
          </p>
          <FadeUp className={`col-span-12 md:col-span-7 md:col-start-5 ${world === "street" ? "f-mono text-base md:text-lg text-zinc-300 uppercase" : "f-serif text-2xl md:text-4xl leading-snug"}`}>
            {t(world === "street" ? "collStreetLead" : "collOldLead")}
          </FadeUp>
        </div>
        <div data-testid="collection-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-16 md:gap-y-28">
          {(items || []).map((p, i) => (
            <ProductCard key={p.id} p={p} index={i} total={items.length} className={LAYOUT[i % LAYOUT.length]} />
          ))}
        </div>
      </section>
      <CrossOver th={th} />
      <Footer />
    </main>
  );
}
